import { useState, useCallback, useRef, useEffect } from "react";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  MoreHorizontal,
  Pencil,
  Trash2,
  Layers3,
  ChevronRight,
  GripVertical,
} from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
} from "@dnd-kit/core";
import type { DragStartEvent, DragMoveEvent, DragEndEvent } from "@dnd-kit/core";

import {
  useStorefrontTenantKey,
  useCollections,
  useDeleteCollection,
  useUpdateCollection,
  useMoveCollection,
  collectionQueryKeys,
  type Collection,
} from "./hooks";
import { CollectionDrawer } from "./CollectionDrawer";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/lib/store/authStore";

// ── Tree utilities ───────────────────────────────────────────

interface TreeNode extends Collection {
  children: TreeNode[];
}

/** Transform flat collection list into a tree based on parent_collection. */
function buildTree(collections: Collection[]): TreeNode[] {
  const map = new Map<string, TreeNode>();
  const roots: TreeNode[] = [];

  for (const c of collections) {
    map.set(c.name, { ...c, children: [] });
  }

  for (const c of collections) {
    const node = map.get(c.name)!;
    const parentName = c.parent_collection;
    if (parentName && map.has(parentName)) {
      map.get(parentName)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  return roots;
}

/** Get all descendant IDs of a node (for circular-drop prevention). */
function getDescendantIds(
  nodeId: string,
  collections: Collection[]
): Set<string> {
  const descendants = new Set<string>();
  const queue = [nodeId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    for (const c of collections) {
      if (c.parent_collection === current && !descendants.has(c.name)) {
        descendants.add(c.name);
        queue.push(c.name);
      }
    }
  }
  return descendants;
}

// ── Drop position type ───────────────────────────────────────

type DropPosition = "before" | "after" | "inside";

interface DropIndicator {
  targetId: string;
  position: DropPosition;
}

// ── Page component ───────────────────────────────────────────

export function CollectionsPage() {
  const { tenantKey, isLoading: tenantLoading } = useStorefrontTenantKey();
  const { data: collections, isLoading: collectionsLoading } =
    useCollections(tenantKey);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingCollection, setEditingCollection] =
    useState<Collection | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Collection | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // DnD state
  const [activeId, setActiveId] = useState<string | null>(null);
  const [dropIndicator, setDropIndicator] = useState<DropIndicator | null>(
    null
  );
  const pointerYRef = useRef(0);
  const descendantsRef = useRef<Set<string>>(new Set());
  const pointerHandlerRef = useRef<((e: PointerEvent) => void) | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const toggleExpanded = useCallback((name: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  const deleteMutation = useDeleteCollection(tenantKey);
  const updateMutation = useUpdateCollection(tenantKey);
  const moveMutation = useMoveCollection(tenantKey);
  const queryClient = useQueryClient();

  const handleCreate = useCallback(() => {
    setEditingCollection(null);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((collection: Collection) => {
    setEditingCollection(collection);
    setDrawerOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    setEditingCollection(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.name);
      toast.success(`Collection '${deleteTarget.title}' deleted.`);
      setDeleteTarget(null);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to delete collection";
      toast.error(message);
    }
  }, [deleteTarget, deleteMutation]);

  const handleToggleVisibility = useCallback(
    async (collection: Collection) => {
      const newValue = !collection.is_visible;

      queryClient.setQueryData(
        collectionQueryKeys.list(tenantKey ?? ""),
        (old: Collection[] | undefined) =>
          old?.map((c) =>
            c.name === collection.name ? { ...c, is_visible: newValue } : c
          )
      );

      try {
        await updateMutation.mutateAsync({
          collection_name: collection.name,
          is_visible: newValue ? 1 : 0,
        });
      } catch {
        queryClient.setQueryData(
          collectionQueryKeys.list(tenantKey ?? ""),
          (old: Collection[] | undefined) =>
            old?.map((c) =>
              c.name === collection.name
                ? { ...c, is_visible: collection.is_visible }
                : c
            )
        );
        toast.error("Could not update visibility.");
      }
    },
    [tenantKey, updateMutation, queryClient]
  );

  // ── DnD handlers ────────────────────────────────────────────

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const id = event.active.id as string;
      setActiveId(id);
      descendantsRef.current = collections
        ? getDescendantIds(id, collections)
        : new Set();

      // Auto-collapse the dragged node
      setExpanded((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });

      // Track pointer position
      const handler = (e: PointerEvent) => {
        pointerYRef.current = e.clientY;
      };
      document.addEventListener("pointermove", handler);
      pointerHandlerRef.current = handler;
    },
    [collections]
  );

  const handleDragMove = useCallback(
    (event: DragMoveEvent) => {
      if (!event.over || !activeId) {
        setDropIndicator(null);
        return;
      }

      const overId = event.over.id as string;

      // No indicator on self or descendants
      if (overId === activeId || descendantsRef.current.has(overId)) {
        setDropIndicator(null);
        return;
      }

      const rect = event.over.rect;
      const relativeY = pointerYRef.current - rect.top;
      const ratio = relativeY / rect.height;

      let position: DropPosition;
      if (ratio < 0.25) position = "before";
      else if (ratio > 0.75) position = "after";
      else position = "inside";

      setDropIndicator({ targetId: overId, position });
    },
    [activeId]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      // Cleanup pointer listener
      if (pointerHandlerRef.current) {
        document.removeEventListener("pointermove", pointerHandlerRef.current);
        pointerHandlerRef.current = null;
      }

      const draggedId = event.active.id as string;

      if (
        !event.over ||
        !dropIndicator ||
        !collections ||
        draggedId === (event.over.id as string)
      ) {
        setActiveId(null);
        setDropIndicator(null);
        return;
      }

      const targetId = event.over.id as string;

      // Reject dropping on own descendants
      if (descendantsRef.current.has(targetId)) {
        setActiveId(null);
        setDropIndicator(null);
        return;
      }

      const target = collections.find((c) => c.name === targetId);
      if (!target) {
        setActiveId(null);
        setDropIndicator(null);
        return;
      }

      let newParent: string | null;
      let newIndex: number;

      if (dropIndicator.position === "inside") {
        newParent = targetId;
        newIndex = 0;
      } else {
        newParent = target.parent_collection;

        const siblings = collections
          .filter((c) => {
            if (newParent) return c.parent_collection === newParent;
            return !c.parent_collection;
          })
          .sort((a, b) => a.display_order - b.display_order);

        const targetIdx = siblings.findIndex((s) => s.name === targetId);
        newIndex =
          dropIndicator.position === "before" ? targetIdx : targetIdx + 1;

        // Adjust if dragged item was before target in same parent
        const draggedIdx = siblings.findIndex((s) => s.name === draggedId);
        if (draggedIdx !== -1 && draggedIdx < newIndex) {
          newIndex--;
        }
      }

      moveMutation.mutate(
        {
          collection_name: draggedId,
          new_parent: newParent,
          new_index: newIndex,
        },
        {
          onError: () => toast.error("Failed to move collection."),
        }
      );

      setActiveId(null);
      setDropIndicator(null);
    },
    [dropIndicator, collections, moveMutation]
  );

  const handleDragCancel = useCallback(() => {
    if (pointerHandlerRef.current) {
      document.removeEventListener("pointermove", pointerHandlerRef.current);
      pointerHandlerRef.current = null;
    }
    setActiveId(null);
    setDropIndicator(null);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pointerHandlerRef.current) {
        document.removeEventListener(
          "pointermove",
          pointerHandlerRef.current
        );
      }
    };
  }, []);

  const activeNode = activeId
    ? collections?.find((c) => c.name === activeId)
    : null;

  // Tenant not configured
  if (!tenantLoading && !tenantKey) {
    return (
      <PageContainer>
        <div className="p-6">
          <h2 className="text-2xl font-semibold mb-2">Collections</h2>
          <p className="text-muted-foreground">
            Storefront is not configured for your account.
          </p>
        </div>
      </PageContainer>
    );
  }

  const isLoading = tenantLoading || collectionsLoading;

  return (
    <PageContainer>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight">
              Collections
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Group products into browsable collections for your storefront
            </p>
          </div>
          <Button onClick={handleCreate} className="rounded">
            <Plus className="mr-2 h-4 w-4" />
            New Collection
          </Button>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="flex items-center gap-4 rounded-md bg-muted/30 p-4"
              >
                <Skeleton className="h-10 w-10 rounded-sm" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-32" />
                </div>
                <Skeleton className="h-6 w-16 rounded-full" />
              </div>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!isLoading && collections && collections.length === 0 && (
          <div className="flex flex-col items-center gap-4 py-16 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted/50">
              <Layers3 className="h-7 w-7 text-muted-foreground/50" />
            </div>
            <div>
              <p className="font-medium">No collections yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Create your first collection to group products for your
                storefront.
              </p>
            </div>
            <Button onClick={handleCreate} className="rounded">
              <Plus className="mr-2 h-4 w-4" />
              New Collection
            </Button>
          </div>
        )}

        {/* Collection tree with DnD */}
        {!isLoading && collections && collections.length > 0 && (
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragMove={handleDragMove}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <div className="rounded-md border bg-card overflow-hidden">
              {buildTree(collections).map((node) => (
                <CollectionTreeRow
                  key={node.name}
                  node={node}
                  depth={0}
                  expanded={expanded}
                  onToggleExpanded={toggleExpanded}
                  onEdit={handleEdit}
                  onDelete={setDeleteTarget}
                  onToggleVisibility={handleToggleVisibility}
                  activeId={activeId}
                  dropIndicator={dropIndicator}
                />
              ))}
            </div>

            <DragOverlay dropAnimation={null}>
              {activeNode && (
                <div className="flex items-center gap-3 rounded-md bg-background border border-primary/20 shadow-lg p-4 opacity-90 w-[400px]">
                  <GripVertical className="h-4 w-4 text-muted-foreground" />
                  <Layers3 className="h-5 w-5 text-muted-foreground/50" />
                  <span className="text-sm font-medium">
                    {activeNode.title}
                  </span>
                </div>
              )}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      {/* Collection drawer */}
      <CollectionDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        collection={editingCollection}
        tenantKey={tenantKey}
      />

      {/* Delete confirmation dialog */}
      <Dialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
      >
        <DialogContent className="max-w-sm rounded-2xl">
          <DialogHeader>
            <DialogTitle>Delete Collection</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{" "}
              <strong>{deleteTarget?.title}</strong>? This action cannot be
              undone. All product assignments will be removed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              disabled={deleteMutation.isPending}
              className="rounded"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
              className="rounded"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}

// ── Tree Row (recursive, with DnD) ──────────────────────────

interface CollectionTreeRowProps {
  node: TreeNode;
  depth: number;
  expanded: Set<string>;
  onToggleExpanded: (name: string) => void;
  onEdit: (c: Collection) => void;
  onDelete: (c: Collection) => void;
  onToggleVisibility: (c: Collection) => void;
  activeId: string | null;
  dropIndicator: DropIndicator | null;
}

function CollectionTreeRow({
  node,
  depth,
  expanded,
  onToggleExpanded,
  onEdit,
  onDelete,
  onToggleVisibility,
  activeId,
  dropIndicator,
}: CollectionTreeRowProps) {
  const tenantServerUrl = useAuthStore((s) => s.tenantServerUrl);
  const hasChildren = node.children.length > 0;
  const isExpanded = expanded.has(node.name);
  const isDragging = activeId === node.name;

  const isDropTarget = dropIndicator?.targetId === node.name;
  const dropPosition = isDropTarget ? dropIndicator.position : null;

  const {
    setNodeRef: setDragRef,
    listeners,
    attributes,
  } = useDraggable({ id: node.name });

  const { setNodeRef: setDropRef } = useDroppable({ id: node.name });

  const combinedRef = useCallback(
    (el: HTMLElement | null) => {
      setDragRef(el);
      setDropRef(el);
    },
    [setDragRef, setDropRef]
  );

  return (
    <>
      <div className="relative">
        {/* Indent guide lines — subtle vertical lines for tree structure */}
        {Array.from({ length: depth }, (_, i) => (
          <div
            key={i}
            className="absolute top-0 bottom-0 w-px bg-border/40"
            style={{ left: 20 + i * 24 }}
          />
        ))}

        {/* Before drop indicator */}
        {dropPosition === "before" && (
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-primary z-10" />
        )}

        <div
          ref={combinedRef}
          className={cn(
            "flex items-center gap-3 border-b py-3 pr-4 transition-colors",
            isDragging
              ? "opacity-30"
              : dropPosition === "inside"
                ? "bg-primary/10"
                : "hover:bg-muted/40"
          )}
          style={{
            paddingLeft: 16 + depth * 24,
            backgroundColor: !isDragging && dropPosition !== "inside" && depth > 0
              ? `hsl(210 20% ${Math.max(98 - depth * 1.5, 93)}%)`
              : undefined,
          }}
        >
          {/* Drag handle */}
          <button
            type="button"
            className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground transition-colors"
            {...listeners}
            {...attributes}
          >
            <GripVertical className="h-4 w-4" />
          </button>

          {/* Expand/collapse */}
          <button
            type="button"
            className={cn(
              "flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md transition-colors",
              hasChildren
                ? "hover:bg-muted cursor-pointer"
                : "invisible"
            )}
            onClick={() => hasChildren && onToggleExpanded(node.name)}
            tabIndex={hasChildren ? 0 : -1}
            aria-label={isExpanded ? "Collapse" : "Expand"}
          >
            <ChevronRight
              className={cn(
                "h-4 w-4 text-muted-foreground transition-transform",
                isExpanded && "rotate-90"
              )}
            />
          </button>

          {/* Thumbnail */}
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-sm bg-muted/40">
            {(node.banner_image || node.fallback_image) ? (
              <img
                src={(() => {
                  const src = node.banner_image || node.fallback_image || "";
                  return src.startsWith("/") && tenantServerUrl ? `${tenantServerUrl}${src}` : src;
                })()}
                alt={node.title}
                className="h-10 w-10 rounded-sm object-cover"
              />
            ) : (
              <Layers3 className="h-5 w-5 text-muted-foreground/40" />
            )}
          </div>

          {/* Info */}
          <button
            type="button"
            className="min-w-0 flex-1 text-left"
            onClick={() => onEdit(node)}
          >
            <p className="text-sm font-medium truncate">{node.title}</p>
            <p className="text-xs text-muted-foreground font-mono truncate">
              {node.slug}
            </p>
          </button>

          {/* Product count */}
          <Badge
            variant="secondary"
            className="rounded-full text-xs font-normal"
          >
            {node.product_count} products
          </Badge>

          {/* Visibility toggle */}
          <Switch
            checked={node.is_visible}
            onCheckedChange={() => onToggleVisibility(node)}
            aria-label={`${node.is_visible ? "Hide" : "Show"} ${node.title}`}
          />

          {/* Overflow menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-sm"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded">
              <DropdownMenuItem onClick={() => onEdit(node)}>
                <Pencil className="mr-2 h-3.5 w-3.5" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onDelete(node)}
                className="text-red-600 focus:text-red-600"
              >
                <Trash2 className="mr-2 h-3.5 w-3.5" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* After drop indicator */}
        {dropPosition === "after" && (
          <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-primary z-10" />
        )}
      </div>

      {/* Render children when expanded */}
      {hasChildren &&
        isExpanded &&
        node.children.map((child) => (
          <CollectionTreeRow
            key={child.name}
            node={child}
            depth={depth + 1}
            expanded={expanded}
            onToggleExpanded={onToggleExpanded}
            onEdit={onEdit}
            onDelete={onDelete}
            onToggleVisibility={onToggleVisibility}
            activeId={activeId}
            dropIndicator={dropIndicator}
          />
        ))}
    </>
  );
}
